/**
 * Formatting for the estimated-wait panel.
 *
 * The wait figures are deliberately ROUGH drain-rate estimates (see
 * EstimatedWaitResponse / DECISIONS.md) — the wording here leans into that:
 * a tilde prefix, "circa", and a `null` wait rendered as "—"/"non stimabile"
 * rather than a false "0". Pure and deterministic; no clock.
 */

import { WaitConfidence } from "@/api/generated/model";

const MINUTE = 60;

/**
 * Italian label for a rough wait in minutes. `null` (not estimable) →
 * "non stimabile". Otherwise a tilde-prefixed, rounded figure: "~5 min",
 * "~1 h", "~2 h 15 min".
 */
export function formatWaitMinutes(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes < 0) {
    return "non stimabile";
  }
  const total = Math.round(minutes);
  if (total < 1) return "~1 min";
  if (total < MINUTE) return `~${total} min`;
  const hours = Math.floor(total / MINUTE);
  const mins = total % MINUTE;
  return mins === 0 ? `~${hours} h` : `~${hours} h ${mins} min`;
}

/** Short label (no tilde) for the most-compact contexts, e.g. "5 min", "1 h 20 min". */
export function shortWait(minutes: number | null): string {
  const full = formatWaitMinutes(minutes);
  return full.startsWith("~") ? full.slice(1) : full;
}

const CONFIDENCE_LABEL: Record<WaitConfidence, string> = {
  [WaitConfidence.none]: "nessuna stima",
  [WaitConfidence.low]: "bassa affidabilità",
  [WaitConfidence.medium]: "media affidabilità",
  [WaitConfidence.high]: "alta affidabilità",
};

/** Italian label for a wait-estimate confidence level. */
export function confidenceLabel(confidence: WaitConfidence): string {
  return CONFIDENCE_LABEL[confidence] ?? CONFIDENCE_LABEL[WaitConfidence.none];
}

/** People-ahead phrasing, singular/plural aware. */
export function peopleAheadLabel(count: number): string {
  if (count <= 0) return "nessuno davanti";
  return count === 1 ? "1 persona davanti" : `${count} persone davanti`;
}
